// Contacts by hand (steps 0.45, 0.46; migration 20261216090000): the shapes the screens send through act (add_contact,
// update_contact, add_firm, update_firm, note_latest, add_contacts) and read back, and the words for them.
import type { SocialKind } from "./socials";

export type Socials = Partial<Record<SocialKind, string>>;

/** A person added by hand. Without organisation or firm they get a one-person firm under their own name. */
export interface ContactInput {
  full_name: string;
  email?: string;
  phone?: string;
  role?: string;
  /** An existing firm's id. */
  organisation?: string;
  /** Or a new (or matching) firm. */
  firm?: { name?: string; website?: string; country?: string };
  /** Their firm's website, when no firm is named. */
  website?: string;
  socials?: Socials;
  linkedin_url?: string;
  source?: string;
  source_detail?: string;
  /** Someone the owner knows (default): never cold-emailed. */
  known?: boolean;
  stage?: string;
  note?: string;
  last_contacted_on?: string;
  next_step?: string;
  next_step_on?: string;
}

/** An edit: only the fields given change; null or "" clears one. A blank social link removes that site. */
export interface ContactEdit {
  full_name?: string;
  role?: string | null;
  email?: string | null;
  phone?: string | null;
  linkedin_url?: string | null;
  socials?: Socials;
  organisation?: string | null;
  firm?: { name: string; website?: string; country?: string };
  source?: string | null;
  source_detail?: string | null;
  known?: boolean;
}

export interface FirmInput {
  name: string;
  website?: string;
  country?: string;
  socials?: Socials;
  phone?: string;
  email?: string;
  known?: boolean;
  stage?: string;
  note?: string;
  source_detail?: string;
}

export interface FirmEdit {
  name?: string;
  website?: string | null;
  country?: string | null;
  socials?: Socials;
  phone?: string | null;
  email?: string | null;
  known?: boolean;
}

/** Where things stand: any of these; null clears the dates and the next step. */
export interface LatestInput {
  note?: string;
  stage?: string;
  last_contacted_on?: string | null;
  next_step?: string | null;
  next_step_on?: string | null;
}

export type AddResult =
  | { status: "created" | "updated"; person_id?: string; organisation_id?: string; firm?: "existing" | "matched" | "created" | "one_person"; matched_on?: string }
  | { status: "duplicate"; person_id?: string; organisation_id?: string; full_name?: string; name?: string; matched_on: string };

export interface AddContactsResult {
  rows: number;
  created: number;
  updated: number;
  skipped: number;
  firms_created: number;
  errors: { row: number; error: string }[];
}

/** What a duplicate matched on, in words. */
export const MATCHED_ON: Record<string, string> = {
  email: "the same email address",
  phone: "the same phone number",
  linkedin: "the same LinkedIn profile",
  instagram: "the same Instagram account",
  facebook: "the same Facebook page",
  tiktok: "the same TikTok account",
  pinterest: "the same Pinterest account",
  x: "the same X account",
  youtube: "the same YouTube channel",
  name_at_firm: "the same name at the same firm",
  website: "the same website",
  name: "the same name",
};

/** "How we know them": the common answers for a hand-added contact (keys from crm_contact_sources). */
export const HOW_WE_KNOW = [
  { key: "personal", label: "Personal network" },
  { key: "event", label: "Event or fair" },
  { key: "introduction", label: "Introduction" },
  { key: "social", label: "Social media" },
  { key: "inbound", label: "They came to us" },
  { key: "other", label: "Other" },
] as const;

/** Today as YYYY-MM-DD in the browser's time zone. */
export function today(now = new Date()): string {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/** Drops blank strings and empty objects, so only what was typed is sent. */
export function compact<T extends object>(o: T): T {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(o)) {
    if (v === undefined || v === null) continue;
    if (typeof v === "string" && !v.trim()) continue;
    if (typeof v === "object" && !Array.isArray(v)) {
      const inner = compact(v as Record<string, unknown>);
      if (!Object.keys(inner).length) continue;
      out[k] = inner;
      continue;
    }
    out[k] = typeof v === "string" ? v.trim() : v;
  }
  return out as T;
}
